import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable, { type TableColumn } from "react-data-table-component";
import { adminTableTheme } from "../../components/admin/adminTableTheme";
import styles from "../../components/admin/Admin.module.css";
import { ErrorState, LoadingState } from "../../components/states";
import { useToast } from "../../components/toast/toastContext";
import { PageIntro } from "../../components/admin/PageIntro";
import { Dropdown } from "../../components/shared/Dropdown";
import {
  fetchAssignableRoles,
  fetchRequestTypeAutoApprovals,
  updateRequestTypeAssignee,
  updateRequestTypeAutoApproval,
  type AssignableRole,
  type AssigneeKind,
  type RequestTypeAutoApproval,
} from "../../api/requestTypeSettings";

const SettingsWritePermission = "settings.write";

/**
 * The "Request types" tab of System Settings (`AIA-81`): every kind of request the portal holds, and a switch
 * for whether a person's own request of that kind is approved as soon as it is raised. Types that are carried
 * out by hand show their switch disabled. Saves and refusals are reported as toasts.
 */
export function RequestTypesContent(): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded"; rows: RequestTypeAutoApproval[] } | { kind: "error"; message: string }>({
    kind: "loading",
  });
  const [pendingType, setPendingType] = useState<string | null>(null);
  const [roles, setRoles] = useState<AssignableRole[]>([]);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const result = await fetchRequestTypeAutoApprovals();
    if (result.kind === "ok") {
      setState({ kind: "loaded", rows: result.response });
    } else if (result.kind === "denied") {
      setState({ kind: "error", message: t("admin.denied.description", { permissions: SettingsWritePermission }) });
    } else {
      setState({ kind: "error", message: result.message });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  // The roles to choose from; a failed read leaves only Unassigned and Group Owner on offer, which the person can still choose.
  useEffect(() => {
    void fetchAssignableRoles().then((result) => {
      if (result.kind === "ok") {
        setRoles(result.response);
      }
    });
  }, []);

  const nameOf = useCallback((requestType: string) => t(`admin.requestTypes.types.${requestType}.name`, { defaultValue: requestType }), [t]);

  const toggle = async (row: RequestTypeAutoApproval, autoApprove: boolean) => {
    setPendingType(row.requestType);
    const result = await updateRequestTypeAutoApproval(row.requestType, autoApprove);
    setPendingType(null);
    if (result.kind === "ok") {
      setState((current) =>
        current.kind === "loaded"
          ? { kind: "loaded", rows: current.rows.map((candidate) => (candidate.requestType === row.requestType ? result.response : candidate)) }
          : current,
      );
      toast.show(t("admin.requestTypes.saved", { name: nameOf(row.requestType) }), "success");
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: SettingsWritePermission }), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  const assign = async (row: RequestTypeAutoApproval, choice: string) => {
    const kind: AssigneeKind = choice === "Unassigned" || choice === "GroupOwner" ? choice : "Role";
    const roleId = kind === "Role" ? choice.slice("role:".length) : null;
    setPendingType(row.requestType);
    const result = await updateRequestTypeAssignee(row.requestType, kind, roleId);
    setPendingType(null);
    if (result.kind === "ok") {
      setState((current) =>
        current.kind === "loaded"
          ? { kind: "loaded", rows: current.rows.map((candidate) => (candidate.requestType === row.requestType ? result.response : candidate)) }
          : current,
      );
      toast.show(t("admin.requestTypes.assigneeSaved", { name: nameOf(row.requestType) }), "success");
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: SettingsWritePermission }), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  const assigneeOptions = (row: RequestTypeAutoApproval) => [
    { value: "Unassigned", label: t("admin.requestTypes.assignee.unassigned") },
    { value: "GroupOwner", label: t("admin.requestTypes.assignee.groupOwner") },
    ...roles.map((role) => ({
      value: `role:${role.roleId}`,
      label: role.name,
      group: t("admin.requestTypes.assignee.rolesGroup"),
    })),
    // A role that has since been removed from the list still shows as the choice.
    ...(row.assigneeKind === "Role" && row.assigneeRoleId !== null && !roles.some((role) => role.roleId === row.assigneeRoleId)
      ? [{ value: `role:${row.assigneeRoleId}`, label: row.assigneeRoleName ?? row.assigneeRoleId, group: t("admin.requestTypes.assignee.rolesGroup") }]
      : []),
  ];

  const columns: TableColumn<RequestTypeAutoApproval>[] = [
      {
        id: "type",
        name: t("admin.requestTypes.table.type"),
        grow: 2,
        cell: (row) => (
          <div>
            <strong>{nameOf(row.requestType)}</strong>
            <br />
            <span className={styles.note}>{t(`admin.requestTypes.types.${row.requestType}.description`, { defaultValue: "" })}</span>
          </div>
        ),
      },
      {
        id: "canBeAutoApproved",
        name: t("admin.requestTypes.table.canBeAutoApproved"),
        selector: (row) => (row.canBeAutoApproved ? 1 : 0),
        sortable: true,
        cell: (row) => (row.canBeAutoApproved ? t("admin.requestTypes.yes") : t("admin.requestTypes.no")),
      },
      {
        id: "autoApprove",
        name: t("admin.requestTypes.table.autoApprove"),
        cell: (row) => (
          <input
            type="checkbox"
            role="switch"
            className={styles.allocationToggle}
            checked={row.autoApprove}
            disabled={!row.canBeAutoApproved || pendingType !== null}
            aria-label={t("admin.requestTypes.autoApproveFor", { name: nameOf(row.requestType) })}
            onChange={(event) => void toggle(row, event.target.checked)}
          />
        ),
      },
      {
        id: "assignee",
        name: t("admin.requestTypes.table.assignee"),
        grow: 2,
        minWidth: "14rem",
        cell: (row) => (
          <Dropdown
            id={`assignee-${row.requestType}`}
            ariaLabel={t("admin.requestTypes.assigneeFor", { name: nameOf(row.requestType) })}
            options={assigneeOptions(row)}
            value={row.assigneeKind === "Role" ? `role:${row.assigneeRoleId ?? ""}` : row.assigneeKind}
            // An auto-approved type has no approver to route to, so its choice is fixed at Unassigned.
            disabled={pendingType !== null || row.autoApprove}
            portal
            onChange={(choice) => void assign(row, choice)}
          />
        ),
      },
      {
        id: "updated",
        name: t("admin.requestTypes.table.updated"),
        grow: 1,
        cell: (row) => (row.updatedAt ? new Date(row.updatedAt).toLocaleString() : t("admin.requestTypes.neverChanged")),
      },
  ];

  if (state.kind === "loading") {
    return <LoadingState label={t("admin.requestTypes.loading")} />;
  }

  if (state.kind === "error") {
    return <ErrorState title={t("admin.requestTypes.errorTitle")} description={state.message} onRetry={() => void load()} />;
  }

  return (
    <section className={styles.section}>
      <PageIntro>{t("admin.requestTypes.explanation")}</PageIntro>
      <div className={`${styles.tableWrapper} ${styles.peopleTableWrapper} ${styles.syncTableWrapper}`}>
        <DataTable
          columns={columns}
          data={state.rows}
          className={styles.peopleDataTable}
          keyField="requestType"
          ariaLabel={t("admin.requestTypes.title")}
          theme={adminTableTheme}
          striped
          highlightOnHover
          columnSeparator="subtle"
        />
      </div>
    </section>
  );
}
